import React, { useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Button,
  ButtonGroup,
  Card,
  Row,
  Col,
  Badge,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
  PieChart,
  Pie,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";
import {
  FiDownload,
  FiAlertCircle,
  FiCheckCircle,
  FiHome,
} from "react-icons/fi";
import { AiOutlineBarChart, AiOutlinePrinter } from "react-icons/ai";
import { settings } from "../../helpers/settings";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

const COLORS = [
  "#69DB7C",
  "#4DABF7",
  "#FFA94D",
  "#FF6B6B",
  "#9775FA",
  "#F783AC",
  "#868E96",
  "#51CF66",
  "#339AF0",
];

const filterOptions = ["daily", "weekly", "monthly", "yearly", "all", "custom"];
const filterLabels = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  yearly: "Yearly",
  all: "All Time",
  custom: "Custom Range",
};

const JiffyReportsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("maya-leander");
  const [selectedFilter, setSelectedFilter] = useState("daily");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateField, setDateField] = useState("commitment_date");

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Format date for API - Use local date, not UTC
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Calculate date range based on filter
  const calculateDateRange = () => {
    const today = new Date();
    let start, end;

    switch (selectedFilter) {
      case "daily":
        // Use selected range or today
        if (startDate && endDate) {
          start = formatDateForAPI(startDate);
          end = formatDateForAPI(endDate);
        } else if (startDate) {
          start = end = formatDateForAPI(startDate);
        } else {
          start = end = formatDateForAPI(today);
        }
        break;
      case "weekly":
        if (startDate) {
          const weekStart = new Date(startDate);
          weekStart.setDate(startDate.getDate() - startDate.getDay() + 1);
          start = formatDateForAPI(weekStart);
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          end = formatDateForAPI(weekEnd);
        } else {
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay() + 1);
          start = formatDateForAPI(weekStart);
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          end = formatDateForAPI(weekEnd);
        }
        break;
      case "monthly":
        const monthDate = startDate || today;
        start = formatDateForAPI(
          new Date(monthDate.getFullYear(), monthDate.getMonth(), 1),
        );
        end = formatDateForAPI(
          new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0),
        );
        break;
      case "yearly":
        const yearDate = startDate || today;
        start = formatDateForAPI(new Date(yearDate.getFullYear(), 0, 1));
        end = formatDateForAPI(new Date(yearDate.getFullYear(), 11, 31));
        break;
      case "all":
        // All time - server will calculate min/max dates
        start = "2020-01-01"; // Fallback start date
        end = formatDateForAPI(today);
        break;
      case "custom":
        if (startDate && endDate) {
          start = formatDateForAPI(startDate);
          end = formatDateForAPI(endDate);
        }
        break;
      default:
        start = end = formatDateForAPI(today);
    }

    return { start, end };
  };

  // Fetch report data
  const fetchReportData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { start, end } = calculateDateRange();

      if (!start || !end) {
        setError("Please select valid date range");
        setLoading(false);
        return;
      }

      // Determine actual report type based on selected dates
      let actualReportType = selectedFilter;
      if (startDate && endDate && selectedFilter !== "custom") {
        // User manually selected a range, treat as custom
        actualReportType = "custom";
      }

      // Determine which PHP file to use based on active tab
      let phpEndpoint = "jiffy_reports.php";
      if (activeTab === "houston") {
        phpEndpoint = "jiffy_reports_houston.php";
      } else if (activeTab === "dallas") {
        phpEndpoint = "jiffy_reports_dallas.php";
      } else if (activeTab === "oklahoma") {
        phpEndpoint = "jiffy_reports_oklahoma.php";
      }

      const url = `${settings.jiffyAPI}/${phpEndpoint}?report_type=${actualReportType}&start_date=${start}&end_date=${end}&date_field=${dateField}&include_issues=true&_t=${Date.now()}`;

      console.log("Fetching report:", {
        location: activeTab,
        endpoint: phpEndpoint,
        reportType: actualReportType,
        selectedFilter: selectedFilter,
        startDate: start,
        endDate: end,
        dateField: dateField,
        url: url,
      });

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        cache: "no-cache",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      console.log("Report data received:", {
        success: data.success,
        period: data.period,
        totalOrders: data.summary?.total_orders,
        hasData: data.summary?.total_orders > 0,
      });

      if (!data.success) {
        throw new Error(data.error || "Failed to fetch report data");
      }

      setReportData(data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching report:", err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on mount and filter change
  useEffect(() => {
    if (selectedFilter === "custom") {
      // Custom requires both dates
      if (startDate && endDate) {
        fetchReportData();
      }
    } else {
      // Other filters can auto-fetch
      fetchReportData();
    }
  }, [selectedFilter, startDate, endDate, dateField, activeTab]);

  // Custom color for bar charts
  const getColor = (index) => COLORS[index % COLORS.length];

  // Format number with commas
  const formatNumber = (num) => {
    return num?.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!reportData) return;

    const doc = new jsPDF();

    // Tab isimleri
    const tabNames = {
      "maya-leander": "Maya (Leander)",
      houston: "Houston",
      dallas: "Dallas",
      oklahoma: "Oklahoma",
    };

    const locationName = tabNames[activeTab] || activeTab;

    // Başlık
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Jiffy Report - " + locationName, 105, 15, { align: "center" });

    // Tarih bilgisi
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Period: ${reportData.period.start} to ${reportData.period.end}`,
      105,
      23,
      { align: "center" },
    );
    doc.text(`Report Type: ${reportData.period.type}`, 105, 29, {
      align: "center",
    });

    let yPos = 40;

    // Genel İstatistikler
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Summary Statistics", 14, yPos);
    yPos += 10;

    doc.autoTable({
      startY: yPos,
      head: [["Metric", "Value"]],
      body: [
        ["Total Orders", formatNumber(reportData.summary.total_orders)],
        ["Total Inches", formatNumber(reportData.summary.total_inches)],
        ["Printed Orders", formatNumber(reportData.summary.printed_orders)],
        ["Not Printed", formatNumber(reportData.summary.not_printed_orders)],
        ["Shipped Orders", formatNumber(reportData.summary.shipped_orders)],
        [
          "Print Completion Rate",
          `${reportData.summary.print_completion_rate}%`,
        ],
      ],
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185] },
      margin: { left: 14 },
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Ortalama Değerler
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Average Values", 14, yPos);
    yPos += 10;

    doc.autoTable({
      startY: yPos,
      head: [["Metric", "Value"]],
      body: [
        ["Avg Orders/Day", formatNumber(reportData.summary.avg_orders_per_day)],
        ["Avg Inches/Day", formatNumber(reportData.summary.avg_inches_per_day)],
        [
          "Avg Inches/Order",
          formatNumber(reportData.summary.avg_inches_per_order),
        ],
      ],
      theme: "striped",
      headStyles: { fillColor: [52, 152, 219] },
      margin: { left: 14 },
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // DTF, UV ve T-Shirt Detayları
    if (reportData.detailed_breakdown) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Product Breakdown", 14, yPos);
      yPos += 10;

      const breakdown = [];

      if (reportData.detailed_breakdown.dtf) {
        breakdown.push([
          "Total DTF Orders",
          formatNumber(reportData.detailed_breakdown.dtf.order_count),
          formatNumber(reportData.detailed_breakdown.dtf.total_inches) +
            " inches",
        ]);
      }

      if (reportData.detailed_breakdown.uv_dtf) {
        breakdown.push([
          "Total UV DTF Orders",
          formatNumber(reportData.detailed_breakdown.uv_dtf.order_count),
          formatNumber(reportData.detailed_breakdown.uv_dtf.total_inches) +
            " inches",
        ]);
      }

      if (reportData.detailed_breakdown.tshirt_assembly) {
        const tshirtInfo =
          reportData.detailed_breakdown.tshirt_assembly.total_tshirts > 0
            ? `${formatNumber(reportData.detailed_breakdown.tshirt_assembly.total_inches)} inches (${formatNumber(reportData.detailed_breakdown.tshirt_assembly.total_tshirts)} t-shirts)`
            : formatNumber(
                reportData.detailed_breakdown.tshirt_assembly.total_inches,
              ) + " inches";

        breakdown.push([
          "T-Shirt Assembly Orders",
          formatNumber(
            reportData.detailed_breakdown.tshirt_assembly.order_count,
          ),
          tshirtInfo,
        ]);
      }

      if (breakdown.length > 0) {
        doc.autoTable({
          startY: yPos,
          head: [["Product Type", "Order Count", "Details"]],
          body: breakdown,
          theme: "striped",
          headStyles: { fillColor: [46, 204, 113] },
          margin: { left: 14 },
        });
      }

      yPos = doc.lastAutoTable.finalY + 10;

      // T-Shirt Detayları
      if (
        reportData.detailed_breakdown.tshirt_assembly &&
        reportData.detailed_breakdown.tshirt_assembly.total_tshirts > 0
      ) {
        // Yeni sayfa gerekirse
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("T-Shirt Assembly Breakdown", 14, yPos);
        yPos += 8;

        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        doc.text(
          `${formatNumber(reportData.detailed_breakdown.tshirt_assembly.order_count)} Orders with ${formatNumber(reportData.detailed_breakdown.tshirt_assembly.total_tshirts)} Total T-Shirts`,
          14,
          yPos,
        );
        yPos += 10;

        const tshirtBreakdown =
          reportData.detailed_breakdown.tshirt_assembly.breakdown;

        // Brand Breakdown
        if (tshirtBreakdown.by_brand && tshirtBreakdown.by_brand.length > 0) {
          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          doc.text("By Brand", 14, yPos);
          yPos += 5;

          doc.autoTable({
            startY: yPos,
            head: [["Brand", "Quantity", "Orders"]],
            body: tshirtBreakdown.by_brand.map((item) => [
              item.brand,
              formatNumber(item.total_quantity),
              formatNumber(item.order_count),
            ]),
            theme: "striped",
            headStyles: { fillColor: [155, 89, 182] },
            margin: { left: 14 },
          });
          yPos = doc.lastAutoTable.finalY + 10;
        }

        // Model Breakdown (Top 10)
        if (tshirtBreakdown.by_model && tshirtBreakdown.by_model.length > 0) {
          if (yPos > 230) {
            doc.addPage();
            yPos = 20;
          }

          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          doc.text("By Model (Top 10)", 14, yPos);
          yPos += 5;

          doc.autoTable({
            startY: yPos,
            head: [["Model", "Quantity", "Orders"]],
            body: tshirtBreakdown.by_model.map((item) => [
              item.model,
              formatNumber(item.total_quantity),
              formatNumber(item.order_count),
            ]),
            theme: "striped",
            headStyles: { fillColor: [52, 152, 219] },
            margin: { left: 14 },
          });
          yPos = doc.lastAutoTable.finalY + 10;
        }

        // Color Breakdown (Top 10)
        if (tshirtBreakdown.by_color && tshirtBreakdown.by_color.length > 0) {
          if (yPos > 230) {
            doc.addPage();
            yPos = 20;
          }

          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          doc.text("By Color (Top 10)", 14, yPos);
          yPos += 5;

          doc.autoTable({
            startY: yPos,
            head: [["Color", "Quantity", "Orders"]],
            body: tshirtBreakdown.by_color.map((item) => [
              item.color,
              formatNumber(item.total_quantity),
              formatNumber(item.order_count),
            ]),
            theme: "striped",
            headStyles: { fillColor: [230, 126, 34] },
            margin: { left: 14 },
          });
          yPos = doc.lastAutoTable.finalY + 10;
        }

        // Size Breakdown
        if (tshirtBreakdown.by_size && tshirtBreakdown.by_size.length > 0) {
          if (yPos > 230) {
            doc.addPage();
            yPos = 20;
          }

          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          doc.text("By Size", 14, yPos);
          yPos += 5;

          doc.autoTable({
            startY: yPos,
            head: [["Size", "Quantity", "Orders"]],
            body: tshirtBreakdown.by_size.map((item) => [
              item.size,
              formatNumber(item.total_quantity),
              formatNumber(item.order_count),
            ]),
            theme: "striped",
            headStyles: { fillColor: [46, 204, 113] },
            margin: { left: 14 },
          });
        }
      }
    }

    // Download
    doc.save(
      `jiffy_report_${locationName}_${reportData.period.start}_${reportData.period.end}.pdf`,
    );
  };

  return (
    <div className="container-fluid py-4">
      <style>{`
        .react-datepicker__day--in-range,
        .react-datepicker__day--in-selecting-range {
          background-color: #4DABF7 !important;
          color: white !important;
        }
        .react-datepicker__day--range-start,
        .react-datepicker__day--range-end {
          background-color: #1971C2 !important;
          color: white !important;
        }
        .react-datepicker__day--selected {
          background-color: #1971C2 !important;
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <AiOutlineBarChart className="me-2" />
          Jiffy Reports
        </h2>
        <Button
          variant="outline-primary"
          onClick={() => navigate("/home")}
          size="lg"
        >
          <FiHome className="me-2" />
          Home
        </Button>
      </div>

      {/* Tabs for different locations */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="maya-leander" title="Maya (Leander)">
          {/* Filter Controls - Maya Location */}
          <Card className="mb-4">
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col lg={3} md={6} sm={12}>
                  <label className="form-label fw-bold">Report Type</label>
                  <ButtonGroup className="w-100 d-flex flex-wrap">
                    {filterOptions.map((filter) => (
                      <Button
                        key={filter}
                        variant={
                          selectedFilter === filter
                            ? "primary"
                            : "outline-primary"
                        }
                        onClick={() => setSelectedFilter(filter)}
                        size="sm"
                        className="flex-fill"
                      >
                        {filterLabels[filter]}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Col>

                {selectedFilter !== "custom" && (
                  <Col lg={3} md={6} sm={12}>
                    <label className="form-label fw-bold">
                      Select Date Range
                    </label>
                    <DatePicker
                      selectsRange={true}
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(update) => {
                        setStartDate(update[0]);
                        setEndDate(update[1]);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      isClearable={true}
                      placeholderText="Select date range"
                    />
                  </Col>
                )}

                {selectedFilter === "custom" && (
                  <>
                    <Col lg={2} md={6} sm={12}>
                      <label className="form-label fw-bold">Start Date</label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                      />
                    </Col>
                    <Col lg={2} md={6} sm={12}>
                      <label className="form-label fw-bold">End Date</label>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                      />
                    </Col>
                  </>
                )}

                <Col lg={2} md={6} sm={12}>
                  <label className="form-label fw-bold">Date Field</label>
                  <select
                    className="form-select"
                    value={dateField}
                    onChange={(e) => setDateField(e.target.value)}
                  >
                    <option value="commitment_date">Commitment Date</option>
                    <option value="email_date">Email Date</option>
                  </select>
                </Col>

                <Col lg={4} md={12} sm={12} className="d-flex gap-2 flex-wrap">
                  {selectedFilter === "custom" && (
                    <Button
                      variant="success"
                      onClick={fetchReportData}
                      disabled={!startDate || !endDate || loading}
                    >
                      Generate Report
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    onClick={exportToPDF}
                    disabled={!reportData}
                  >
                    <FiDownload className="me-1" />
                    Export PDF
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading report data...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <Alert variant="danger">
              <FiAlertCircle className="me-2" />
              {error}
            </Alert>
          )}

          {/* Report Content */}
          {!loading && !error && reportData && (
            <>
              {/* Summary Cards */}
              <Row className="mb-4 g-3">
                <Col xl={3} lg={6} md={6} sm={12}>
                  <Card className="text-center border-primary">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Total Orders</h6>
                      <h2 className="mb-0 text-primary">
                        {formatNumber(reportData.summary.total_orders)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={3} lg={6} md={6} sm={12}>
                  <Card className="text-center border-success">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Total Inches</h6>
                      <h2 className="mb-0 text-success">
                        {formatNumber(reportData.summary.total_inches)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={3} lg={6} md={6} sm={12}>
                  <Card className="text-center border-info">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Printed Orders</h6>
                      <h2 className="mb-0 text-info">
                        {formatNumber(reportData.summary.printed_orders)}
                      </h2>
                      <small className="text-muted">
                        {reportData.summary.print_completion_rate}% completion
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={3} lg={6} md={6} sm={12}>
                  <Card className="text-center border-warning">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Not Printed</h6>
                      <h2 className="mb-0 text-warning">
                        {formatNumber(reportData.summary.not_printed_orders)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Additional Stats */}
              <Row className="mb-4 g-3">
                <Col xl={3} lg={6} md={6} sm={12}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Avg Orders/Day</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.avg_orders_per_day)}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={3} lg={6} md={6} sm={12}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Avg Inches/Day</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.avg_inches_per_day)}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={3} lg={6} md={6} sm={12}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Avg Inches/Order</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.avg_inches_per_order)}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={3} lg={6} md={6} sm={12}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Shipped Orders</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.shipped_orders)}{" "}
                        <small className="text-muted">
                          ({reportData.summary.shipping_completion_rate}%)
                        </small>
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Detailed Order Type Breakdown - Maya Only */}
              {reportData.detailed_breakdown && (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card className="border-info">
                      <Card.Header className="bg-info text-white">
                        <strong>📊 Detailed Order Type Breakdown</strong>
                      </Card.Header>
                      <Card.Body>
                        <Row className="g-3">
                          <Col lg={4} md={6} sm={12}>
                            <Card className="text-center border-success mb-3">
                              <Card.Body>
                                <h6 className="text-muted mb-2">
                                  Total DTF Orders
                                </h6>
                                <h3 className="mb-1 text-success">
                                  {formatNumber(
                                    reportData.detailed_breakdown.dtf
                                      .order_count,
                                  )}
                                </h3>
                                <small className="text-muted">
                                  {formatNumber(
                                    reportData.detailed_breakdown.dtf
                                      .total_inches,
                                  )}{" "}
                                  inches
                                </small>
                                <div className="mt-2">
                                  <small className="text-muted fst-italic">
                                    Includes: Pure DTF + DTF from mixed +
                                    Assembly DTF
                                  </small>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col lg={4} md={6} sm={12}>
                            <Card className="text-center border-primary mb-3">
                              <Card.Body>
                                <h6 className="text-muted mb-2">
                                  Total UV DTF Orders
                                </h6>
                                <h3 className="mb-1 text-primary">
                                  {formatNumber(
                                    reportData.detailed_breakdown.uv_dtf
                                      .order_count,
                                  )}
                                </h3>
                                <small className="text-muted">
                                  {formatNumber(
                                    reportData.detailed_breakdown.uv_dtf
                                      .total_inches,
                                  )}{" "}
                                  inches
                                </small>
                                <div className="mt-2">
                                  <small className="text-muted fst-italic">
                                    Includes: Pure UV DTF + UV from mixed orders
                                  </small>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col lg={4} md={12} sm={12}>
                            <Card className="text-center border-warning mb-3">
                              <Card.Body>
                                <h6 className="text-muted mb-3">
                                  T-Shirt Assembly Orders
                                </h6>
                                <div className="mb-3">
                                  <h3 className="mb-1 text-warning">
                                    {formatNumber(
                                      reportData.detailed_breakdown
                                        .tshirt_assembly.order_count,
                                    )}
                                  </h3>
                                  <small className="text-muted fw-bold">
                                    Orders
                                  </small>
                                </div>
                                {reportData.detailed_breakdown.tshirt_assembly
                                  .total_tshirts > 0 && (
                                  <div className="mb-3 pb-2 border-bottom">
                                    <h2 className="mb-1 text-dark">
                                      {formatNumber(
                                        reportData.detailed_breakdown
                                          .tshirt_assembly.total_tshirts,
                                      )}
                                    </h2>
                                    <small className="text-muted fw-bold">
                                      Total T-Shirts (Items)
                                    </small>
                                  </div>
                                )}
                                <div>
                                  <small className="text-muted">
                                    {formatNumber(
                                      reportData.detailed_breakdown
                                        .tshirt_assembly.total_inches,
                                    )}{" "}
                                    inches
                                  </small>
                                  <div className="mt-1">
                                    <small className="text-muted fst-italic">
                                      DTF + Assembly for Print
                                    </small>
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>

                        {/* T-Shirt Details Breakdown */}
                        {reportData.detailed_breakdown.tshirt_assembly &&
                          reportData.detailed_breakdown.tshirt_assembly
                            .total_tshirts > 0 &&
                          reportData.detailed_breakdown.tshirt_assembly
                            .breakdown && (
                            <Row className="mt-3 g-3">
                              <Col md={12}>
                                <h5 className="mb-3 text-muted">
                                  📦 T-Shirt Details
                                </h5>
                              </Col>

                              {/* By Brand */}
                              {reportData.detailed_breakdown.tshirt_assembly
                                .breakdown.by_brand &&
                                reportData.detailed_breakdown.tshirt_assembly
                                  .breakdown.by_brand.length > 0 && (
                                  <Col lg={6} md={12}>
                                    <Card className="border-secondary">
                                      <Card.Header className="bg-secondary text-white">
                                        <strong>By Brand</strong>
                                      </Card.Header>
                                      <Card.Body
                                        style={{
                                          maxHeight: "250px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        <Table striped bordered hover size="sm">
                                          <thead>
                                            <tr>
                                              <th>Brand</th>
                                              <th className="text-end">Qty</th>
                                              <th className="text-end">
                                                Orders
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {reportData.detailed_breakdown.tshirt_assembly.breakdown.by_brand.map(
                                              (item, idx) => (
                                                <tr key={idx}>
                                                  <td>{item.brand}</td>
                                                  <td className="text-end">
                                                    {formatNumber(
                                                      item.total_quantity,
                                                    )}
                                                  </td>
                                                  <td className="text-end">
                                                    {formatNumber(
                                                      item.order_count,
                                                    )}
                                                  </td>
                                                </tr>
                                              ),
                                            )}
                                          </tbody>
                                        </Table>
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                )}

                              {/* By Model */}
                              {reportData.detailed_breakdown.tshirt_assembly
                                .breakdown.by_model &&
                                reportData.detailed_breakdown.tshirt_assembly
                                  .breakdown.by_model.length > 0 && (
                                  <Col lg={6} md={12}>
                                    <Card className="border-info">
                                      <Card.Header className="bg-info text-white">
                                        <strong>By Model (Top 10)</strong>
                                      </Card.Header>
                                      <Card.Body
                                        style={{
                                          maxHeight: "250px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        <Table striped bordered hover size="sm">
                                          <thead>
                                            <tr>
                                              <th>Model</th>
                                              <th className="text-end">Qty</th>
                                              <th className="text-end">
                                                Orders
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {reportData.detailed_breakdown.tshirt_assembly.breakdown.by_model.map(
                                              (item, idx) => (
                                                <tr key={idx}>
                                                  <td>{item.model}</td>
                                                  <td className="text-end">
                                                    {formatNumber(
                                                      item.total_quantity,
                                                    )}
                                                  </td>
                                                  <td className="text-end">
                                                    {formatNumber(
                                                      item.order_count,
                                                    )}
                                                  </td>
                                                </tr>
                                              ),
                                            )}
                                          </tbody>
                                        </Table>
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                )}

                              {/* By Color */}
                              {reportData.detailed_breakdown.tshirt_assembly
                                .breakdown.by_color &&
                                reportData.detailed_breakdown.tshirt_assembly
                                  .breakdown.by_color.length > 0 && (
                                  <Col lg={6} md={12}>
                                    <Card className="border-warning">
                                      <Card.Header className="bg-warning text-dark">
                                        <strong>By Color (Top 10)</strong>
                                      </Card.Header>
                                      <Card.Body
                                        style={{
                                          maxHeight: "250px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        <Table striped bordered hover size="sm">
                                          <thead>
                                            <tr>
                                              <th>Color</th>
                                              <th className="text-end">Qty</th>
                                              <th className="text-end">
                                                Orders
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {reportData.detailed_breakdown.tshirt_assembly.breakdown.by_color.map(
                                              (item, idx) => (
                                                <tr key={idx}>
                                                  <td>{item.color}</td>
                                                  <td className="text-end">
                                                    {formatNumber(
                                                      item.total_quantity,
                                                    )}
                                                  </td>
                                                  <td className="text-end">
                                                    {formatNumber(
                                                      item.order_count,
                                                    )}
                                                  </td>
                                                </tr>
                                              ),
                                            )}
                                          </tbody>
                                        </Table>
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                )}

                              {/* By Size */}
                              {reportData.detailed_breakdown.tshirt_assembly
                                .breakdown.by_size &&
                                reportData.detailed_breakdown.tshirt_assembly
                                  .breakdown.by_size.length > 0 && (
                                  <Col lg={6} md={12}>
                                    <Card className="border-success">
                                      <Card.Header className="bg-success text-white">
                                        <strong>By Size</strong>
                                      </Card.Header>
                                      <Card.Body
                                        style={{
                                          maxHeight: "250px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        <Table striped bordered hover size="sm">
                                          <thead>
                                            <tr>
                                              <th>Size</th>
                                              <th className="text-end">Qty</th>
                                              <th className="text-end">
                                                Orders
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {reportData.detailed_breakdown.tshirt_assembly.breakdown.by_size.map(
                                              (item, idx) => (
                                                <tr key={idx}>
                                                  <td>{item.size}</td>
                                                  <td className="text-end">
                                                    {formatNumber(
                                                      item.total_quantity,
                                                    )}
                                                  </td>
                                                  <td className="text-end">
                                                    {formatNumber(
                                                      item.order_count,
                                                    )}
                                                  </td>
                                                </tr>
                                              ),
                                            )}
                                          </tbody>
                                        </Table>
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                )}
                            </Row>
                          )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Charts Row */}
              <Row className="mb-4 g-3">
                {/* Printer Distribution */}
                <Col lg={6} md={12} sm={12}>
                  <Card>
                    <Card.Header className="bg-primary text-white">
                      <AiOutlinePrinter className="me-2" />
                      Orders by Printer
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={reportData.by_printer}>
                          <XAxis
                            dataKey="printer"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                          />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="order_count" fill="#4DABF7">
                            {reportData.by_printer.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                            <LabelList dataKey="order_count" position="top" />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Order Type Distribution */}
                <Col lg={6} md={12} sm={12}>
                  <Card>
                    <Card.Header className="bg-success text-white">
                      Orders by Type
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={reportData.by_order_type}
                            dataKey="order_count"
                            nameKey="order_type"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={(entry) =>
                              `${entry.order_type}: ${entry.order_count}`
                            }
                          >
                            {reportData.by_order_type.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Inches by Printer */}
              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-info text-white">
                      Inches Printed by Printer
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={reportData.by_printer}>
                          <XAxis
                            dataKey="printer"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                          />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="total_inches" fill="#51CF66">
                            {reportData.by_printer.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                            <LabelList
                              dataKey="total_inches"
                              position="top"
                              formatter={(value) => value.toFixed(0)}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Inches by Order Type */}
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-warning text-dark">
                      Inches by Order Type
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={reportData.by_order_type}>
                          <XAxis
                            dataKey="order_type"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                          />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="total_inches" fill="#FFA94D">
                            {reportData.by_order_type.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                            <LabelList
                              dataKey="total_inches"
                              position="top"
                              formatter={(value) => value.toFixed(0)}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Daily Trend */}
              {reportData.daily_breakdown.length > 1 && (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card>
                      <Card.Header className="bg-dark text-white">
                        Daily Trend - Orders & Inches
                      </Card.Header>
                      <Card.Body>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={reportData.daily_breakdown}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis yAxisId="left" />
                            <YAxis yAxisId="right" orientation="right" />
                            <Tooltip />
                            <Legend />
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="order_count"
                              stroke="#4DABF7"
                              strokeWidth={2}
                              name="Orders"
                            />
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey="total_inches"
                              stroke="#51CF66"
                              strokeWidth={2}
                              name="Inches"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Detailed Tables */}
              <Row className="mb-4">
                {/* Printer Details */}
                <Col md={6}>
                  <Card>
                    <Card.Header>Printer Performance Details</Card.Header>
                    <Card.Body
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <Table striped bordered hover size="sm">
                        <thead className="sticky-top bg-light">
                          <tr>
                            <th>Printer</th>
                            <th className="text-end">Orders</th>
                            <th className="text-end">Inches</th>
                            <th className="text-end">Avg/Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.by_printer.map((printer, idx) => (
                            <tr key={idx}>
                              <td>
                                {printer.printer === "Not Printed" ? (
                                  <Badge bg="warning" text="dark">
                                    {printer.printer}
                                  </Badge>
                                ) : (
                                  printer.printer
                                )}
                              </td>
                              <td className="text-end">
                                {formatNumber(printer.order_count)}
                                <small className="text-muted ms-1">
                                  ({printer.percentage_of_orders}%)
                                </small>
                              </td>
                              <td className="text-end">
                                {formatNumber(printer.total_inches)}
                              </td>
                              <td className="text-end">
                                {formatNumber(printer.avg_inches_per_order)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Order Type Details */}
                <Col md={6}>
                  <Card>
                    <Card.Header>Order Type Details</Card.Header>
                    <Card.Body
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <Table striped bordered hover size="sm">
                        <thead className="sticky-top bg-light">
                          <tr>
                            <th>Order Type</th>
                            <th className="text-end">Orders</th>
                            <th className="text-end">Inches</th>
                            <th className="text-end">Avg/Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.by_order_type.map((type, idx) => (
                            <tr key={idx}>
                              <td>{type.order_type}</td>
                              <td className="text-end">
                                {formatNumber(type.order_count)}
                                <small className="text-muted ms-1">
                                  ({type.percentage_of_orders}%)
                                </small>
                              </td>
                              <td className="text-end">
                                {formatNumber(type.total_inches)}
                              </td>
                              <td className="text-end">
                                {formatNumber(type.avg_inches_per_order)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Issues Section */}
              {reportData.issues &&
                reportData.issues.summary.total_issues > 0 && (
                  <Row className="mb-4">
                    <Col md={12}>
                      <Card border="danger">
                        <Card.Header className="bg-danger text-white">
                          <FiAlertCircle className="me-2" />
                          Data Quality Issues (
                          {reportData.issues.summary.total_issues})
                        </Card.Header>
                        <Card.Body>
                          <Row>
                            {/* Duplicates */}
                            {reportData.issues.duplicate_pos.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Duplicate POs (
                                  {reportData.issues.duplicate_pos.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                        <th>Count</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.duplicate_pos.map(
                                        (dup, idx) => (
                                          <tr key={idx}>
                                            <td>{dup.po_number}</td>
                                            <td>
                                              <Badge bg="danger">
                                                {dup.count}
                                              </Badge>
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}

                            {/* Missing Data */}
                            {reportData.issues.missing_data.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Missing Data (
                                  {reportData.issues.missing_data.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                        <th>Missing Fields</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.missing_data.map(
                                        (missing, idx) => (
                                          <tr key={idx}>
                                            <td>{missing.po_number}</td>
                                            <td>
                                              {missing.missing_fields.join(
                                                ", ",
                                              )}
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}

                            {/* Late Orders */}
                            {reportData.issues.late_orders.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Late Orders (Not Printed) (
                                  {reportData.issues.late_orders.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                        <th>Days Late</th>
                                        <th>Commitment Date</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.late_orders.map(
                                        (late, idx) => (
                                          <tr key={idx}>
                                            <td>{late.po_number}</td>
                                            <td>
                                              <Badge bg="danger">
                                                {late.days_late} days
                                              </Badge>
                                            </td>
                                            <td>{late.commitment_date}</td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}

                            {/* Orphaned Links */}
                            {reportData.issues.orphaned_links.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Orphaned Links (
                                  {reportData.issues.orphaned_links.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.orphaned_links.map(
                                        (orphan, idx) => (
                                          <tr key={idx}>
                                            <td>{orphan.po_number}</td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}

              {/* Success Message if no issues */}
              {reportData.issues &&
                reportData.issues.summary.total_issues === 0 && (
                  <Alert variant="success">
                    <FiCheckCircle className="me-2" />
                    No data quality issues found in this report period!
                  </Alert>
                )}
            </>
          )}
        </Tab>

        {/* Houston location */}
        <Tab eventKey="houston" title="Houston">
          {/* Filters */}
          <Card className="mb-4">
            <Card.Body>
              <Row className="align-items-end">
                <Col md={3}>
                  <label className="form-label fw-bold">Report Type</label>
                  <ButtonGroup className="w-100">
                    {filterOptions.map((filter) => (
                      <Button
                        key={filter}
                        variant={
                          selectedFilter === filter
                            ? "primary"
                            : "outline-primary"
                        }
                        onClick={() => setSelectedFilter(filter)}
                        size="sm"
                      >
                        {filterLabels[filter]}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Col>

                {selectedFilter !== "custom" && (
                  <Col md={3}>
                    <label className="form-label fw-bold">
                      Select Date Range
                    </label>
                    <DatePicker
                      selectsRange={true}
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(update) => {
                        setStartDate(update[0]);
                        setEndDate(update[1]);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      isClearable={true}
                      placeholderText="Select date range"
                      dayClassName={(date) => {
                        if (!startDate || !endDate) return undefined;
                        const time = date.getTime();
                        const start = startDate.getTime();
                        const end = endDate.getTime();
                        if (time === start || time === end) {
                          return "react-datepicker__day--range-endpoint";
                        }
                        if (time > start && time < end) {
                          return "react-datepicker__day--in-range";
                        }
                        return undefined;
                      }}
                    />
                  </Col>
                )}

                {selectedFilter === "custom" && (
                  <>
                    <Col md={2}>
                      <label className="form-label fw-bold">Start Date</label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                      />
                    </Col>
                    <Col md={2}>
                      <label className="form-label fw-bold">End Date</label>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                      />
                    </Col>
                  </>
                )}

                <Col md={2}>
                  <label className="form-label fw-bold">Date Field</label>
                  <select
                    className="form-select"
                    value={dateField}
                    onChange={(e) => setDateField(e.target.value)}
                  >
                    <option value="commitment_date">Commitment Date</option>
                    <option value="email_date">Email Date</option>
                  </select>
                </Col>

                <Col md={3} className="d-flex gap-2">
                  {selectedFilter === "custom" && (
                    <Button
                      variant="success"
                      onClick={fetchReportData}
                      disabled={!startDate || !endDate || loading}
                    >
                      Generate Report
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    onClick={exportToPDF}
                    disabled={!reportData}
                  >
                    <FiDownload className="me-1" />
                    Export PDF
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading report data...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <Alert variant="danger">
              <FiAlertCircle className="me-2" />
              {error}
            </Alert>
          )}

          {/* Report Content */}
          {!loading && !error && reportData && (
            <>
              {/* Summary Cards */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center border-primary">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Total Orders</h6>
                      <h2 className="mb-0 text-primary">
                        {formatNumber(reportData.summary.total_orders)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-success">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Total Inches</h6>
                      <h2 className="mb-0 text-success">
                        {formatNumber(reportData.summary.total_inches)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-info">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Printed Orders</h6>
                      <h2 className="mb-0 text-info">
                        {formatNumber(reportData.summary.printed_orders)}
                      </h2>
                      <small className="text-muted">
                        {reportData.summary.print_completion_rate}% completion
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-warning">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Not Printed</h6>
                      <h2 className="mb-0 text-warning">
                        {formatNumber(reportData.summary.not_printed_orders)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Additional Stats */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Avg Orders/Day</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.avg_orders_per_day)}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Avg Inches/Day</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.avg_inches_per_day)}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Avg Inches/Order</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.avg_inches_per_order)}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Shipped Orders</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.shipped_orders)}{" "}
                        <small className="text-muted">
                          ({reportData.summary.shipping_completion_rate}%)
                        </small>
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Charts Row */}
              <Row className="mb-4">
                {/* Printer Distribution */}
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-primary text-white">
                      <AiOutlinePrinter className="me-2" />
                      Orders by Printer
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={reportData.by_printer}>
                          <XAxis
                            dataKey="printer"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                          />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="order_count" fill="#4DABF7">
                            {reportData.by_printer.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                            <LabelList dataKey="order_count" position="top" />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Order Type Distribution */}
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-success text-white">
                      Orders by Type
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={reportData.by_order_type}
                            dataKey="order_count"
                            nameKey="order_type"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={(entry) =>
                              `${entry.order_type}: ${entry.order_count}`
                            }
                          >
                            {reportData.by_order_type.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Inches by Printer */}
              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-info text-white">
                      Inches Printed by Printer
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={reportData.by_printer}>
                          <XAxis
                            dataKey="printer"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                          />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="total_inches" fill="#51CF66">
                            {reportData.by_printer.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                            <LabelList
                              dataKey="total_inches"
                              position="top"
                              formatter={(value) => value.toFixed(0)}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Inches by Order Type */}
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-warning text-dark">
                      Inches by Order Type
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={reportData.by_order_type}>
                          <XAxis
                            dataKey="order_type"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                          />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="total_inches" fill="#FFA94D">
                            {reportData.by_order_type.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                            <LabelList
                              dataKey="total_inches"
                              position="top"
                              formatter={(value) => value.toFixed(0)}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Daily Trend */}
              {reportData.daily_breakdown.length > 1 && (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card>
                      <Card.Header className="bg-dark text-white">
                        Daily Trend - Orders & Inches
                      </Card.Header>
                      <Card.Body>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={reportData.daily_breakdown}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis yAxisId="left" />
                            <YAxis yAxisId="right" orientation="right" />
                            <Tooltip />
                            <Legend />
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="order_count"
                              stroke="#4DABF7"
                              strokeWidth={2}
                              name="Orders"
                            />
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey="total_inches"
                              stroke="#51CF66"
                              strokeWidth={2}
                              name="Inches"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Detailed Tables */}
              <Row className="mb-4">
                {/* Printer Details */}
                <Col md={6}>
                  <Card>
                    <Card.Header>Printer Performance Details</Card.Header>
                    <Card.Body
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <Table striped bordered hover size="sm">
                        <thead className="sticky-top bg-light">
                          <tr>
                            <th>Printer</th>
                            <th className="text-end">Orders</th>
                            <th className="text-end">Inches</th>
                            <th className="text-end">Avg/Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.by_printer.map((printer, idx) => (
                            <tr key={idx}>
                              <td>
                                {printer.printer === "Not Printed" ? (
                                  <Badge bg="warning" text="dark">
                                    {printer.printer}
                                  </Badge>
                                ) : (
                                  printer.printer
                                )}
                              </td>
                              <td className="text-end">
                                {formatNumber(printer.order_count)}
                                <small className="text-muted ms-1">
                                  ({printer.percentage_of_orders}%)
                                </small>
                              </td>
                              <td className="text-end">
                                {formatNumber(printer.total_inches)}
                              </td>
                              <td className="text-end">
                                {formatNumber(printer.avg_inches_per_order)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Order Type Details */}
                <Col md={6}>
                  <Card>
                    <Card.Header>Order Type Details</Card.Header>
                    <Card.Body
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <Table striped bordered hover size="sm">
                        <thead className="sticky-top bg-light">
                          <tr>
                            <th>Order Type</th>
                            <th className="text-end">Orders</th>
                            <th className="text-end">Inches</th>
                            <th className="text-end">Avg/Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.by_order_type.map((type, idx) => (
                            <tr key={idx}>
                              <td>{type.order_type}</td>
                              <td className="text-end">
                                {formatNumber(type.order_count)}
                                <small className="text-muted ms-1">
                                  ({type.percentage_of_orders}%)
                                </small>
                              </td>
                              <td className="text-end">
                                {formatNumber(type.total_inches)}
                              </td>
                              <td className="text-end">
                                {formatNumber(type.avg_inches_per_order)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Issues Section */}
              {reportData.issues &&
                reportData.issues.summary.total_issues > 0 && (
                  <Row className="mb-4">
                    <Col md={12}>
                      <Card border="danger">
                        <Card.Header className="bg-danger text-white">
                          <FiAlertCircle className="me-2" />
                          Data Quality Issues (
                          {reportData.issues.summary.total_issues})
                        </Card.Header>
                        <Card.Body>
                          <Row>
                            {/* Duplicates */}
                            {reportData.issues.duplicate_pos.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Duplicate POs (
                                  {reportData.issues.duplicate_pos.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                        <th>Count</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.duplicate_pos.map(
                                        (dup, idx) => (
                                          <tr key={idx}>
                                            <td>{dup.po_number}</td>
                                            <td>
                                              <Badge bg="danger">
                                                {dup.count}
                                              </Badge>
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}

                            {/* Missing Data */}
                            {reportData.issues.missing_data.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Missing Data (
                                  {reportData.issues.missing_data.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                        <th>Missing Fields</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.missing_data.map(
                                        (missing, idx) => (
                                          <tr key={idx}>
                                            <td>{missing.po_number}</td>
                                            <td>
                                              {missing.missing_fields.join(
                                                ", ",
                                              )}
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}

                            {/* Late Orders */}
                            {reportData.issues.late_orders.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Late Orders (Not Printed) (
                                  {reportData.issues.late_orders.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                        <th>Days Late</th>
                                        <th>Commitment Date</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.late_orders.map(
                                        (late, idx) => (
                                          <tr key={idx}>
                                            <td>{late.po_number}</td>
                                            <td>
                                              <Badge bg="danger">
                                                {late.days_late} days
                                              </Badge>
                                            </td>
                                            <td>{late.commitment_date}</td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}

                            {/* Orphaned Links */}
                            {reportData.issues.orphaned_links.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Orphaned Links (
                                  {reportData.issues.orphaned_links.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.orphaned_links.map(
                                        (orphan, idx) => (
                                          <tr key={idx}>
                                            <td>{orphan.po_number}</td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}

              {/* Success Message if no issues */}
              {reportData.issues &&
                reportData.issues.summary.total_issues === 0 && (
                  <Alert variant="success">
                    <FiCheckCircle className="me-2" />
                    No data quality issues found in this report period!
                  </Alert>
                )}
            </>
          )}
        </Tab>

        {/* Dallas location - Full content same as Houston */}
        <Tab eventKey="dallas" title="Dallas">
          {/* Filters */}
          <Card className="mb-4">
            <Card.Body>
              <Row className="align-items-end">
                <Col md={3}>
                  <label className="form-label fw-bold">Report Type</label>
                  <ButtonGroup className="w-100">
                    {filterOptions.map((filter) => (
                      <Button
                        key={filter}
                        variant={
                          selectedFilter === filter
                            ? "primary"
                            : "outline-primary"
                        }
                        onClick={() => setSelectedFilter(filter)}
                        size="sm"
                      >
                        {filterLabels[filter]}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Col>

                {selectedFilter !== "custom" && (
                  <Col md={3}>
                    <label className="form-label fw-bold">
                      Select Date Range
                    </label>
                    <DatePicker
                      selectsRange={true}
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(update) => {
                        setStartDate(update[0]);
                        setEndDate(update[1]);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      isClearable={true}
                      placeholderText="Select date range"
                      dayClassName={(date) => {
                        if (!startDate || !endDate) return undefined;
                        const time = date.getTime();
                        const start = startDate.getTime();
                        const end = endDate.getTime();
                        if (time === start || time === end) {
                          return "react-datepicker__day--range-endpoint";
                        }
                        if (time > start && time < end) {
                          return "react-datepicker__day--in-range";
                        }
                        return undefined;
                      }}
                    />
                  </Col>
                )}

                {selectedFilter === "custom" && (
                  <>
                    <Col md={2}>
                      <label className="form-label fw-bold">Start Date</label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                      />
                    </Col>
                    <Col md={2}>
                      <label className="form-label fw-bold">End Date</label>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                      />
                    </Col>
                  </>
                )}

                <Col md={2}>
                  <label className="form-label fw-bold">Date Field</label>
                  <select
                    className="form-select"
                    value={dateField}
                    onChange={(e) => setDateField(e.target.value)}
                  >
                    <option value="commitment_date">Commitment Date</option>
                    <option value="email_date">Email Date</option>
                  </select>
                </Col>

                <Col md={2} className="d-flex gap-2">
                  {selectedFilter === "custom" && (
                    <Button
                      variant="success"
                      onClick={fetchReportData}
                      disabled={!startDate || !endDate || loading}
                    >
                      Generate Report
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    onClick={exportToPDF}
                    disabled={!reportData}
                  >
                    <FiDownload className="me-1" />
                    Export PDF
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading report data...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <Alert variant="danger">
              <FiAlertCircle className="me-2" />
              {error}
            </Alert>
          )}

          {/* Report Content */}
          {!loading && !error && reportData && (
            <>
              {/* Summary Cards */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center border-primary">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Total Orders</h6>
                      <h2 className="mb-0 text-primary">
                        {formatNumber(reportData.summary.total_orders)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-success">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Total Inches</h6>
                      <h2 className="mb-0 text-success">
                        {formatNumber(reportData.summary.total_inches)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-info">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Printed Orders</h6>
                      <h2 className="mb-0 text-info">
                        {formatNumber(reportData.summary.printed_orders)}
                      </h2>
                      <small className="text-muted">
                        {reportData.summary.print_completion_rate}% completion
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-warning">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Not Printed</h6>
                      <h2 className="mb-0 text-warning">
                        {formatNumber(reportData.summary.not_printed_orders)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Additional Stats */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Avg Orders/Day</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.avg_orders_per_day)}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Avg Inches/Day</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.avg_inches_per_day)}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Avg Inches/Order</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.avg_inches_per_order)}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Shipped Orders</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.shipped_orders)}{" "}
                        <small className="text-muted">
                          ({reportData.summary.shipping_completion_rate}%)
                        </small>
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Charts Row */}
              <Row className="mb-4">
                {/* Printer Distribution */}
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-primary text-white">
                      <AiOutlinePrinter className="me-2" />
                      Orders by Printer
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={reportData.by_printer}>
                          <XAxis
                            dataKey="printer"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                          />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="order_count" fill="#4DABF7">
                            {reportData.by_printer.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                            <LabelList dataKey="order_count" position="top" />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Order Type Distribution */}
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-success text-white">
                      Orders by Type
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={reportData.by_order_type}
                            dataKey="order_count"
                            nameKey="order_type"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={(entry) =>
                              `${entry.order_type}: ${entry.order_count}`
                            }
                          >
                            {reportData.by_order_type.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Inches by Printer */}
              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-info text-white">
                      Inches Printed by Printer
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={reportData.by_printer}>
                          <XAxis
                            dataKey="printer"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                          />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="total_inches" fill="#51CF66">
                            {reportData.by_printer.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                            <LabelList
                              dataKey="total_inches"
                              position="top"
                              formatter={(value) => value.toFixed(0)}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Inches by Order Type */}
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-warning text-dark">
                      Inches by Order Type
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={reportData.by_order_type}>
                          <XAxis
                            dataKey="order_type"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                          />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="total_inches" fill="#FFA94D">
                            {reportData.by_order_type.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                            <LabelList
                              dataKey="total_inches"
                              position="top"
                              formatter={(value) => value.toFixed(0)}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Daily Trend */}
              {reportData.daily_breakdown.length > 1 && (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card>
                      <Card.Header className="bg-dark text-white">
                        Daily Trend - Orders & Inches
                      </Card.Header>
                      <Card.Body>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={reportData.daily_breakdown}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis yAxisId="left" />
                            <YAxis yAxisId="right" orientation="right" />
                            <Tooltip />
                            <Legend />
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="order_count"
                              stroke="#4DABF7"
                              strokeWidth={2}
                              name="Orders"
                            />
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey="total_inches"
                              stroke="#51CF66"
                              strokeWidth={2}
                              name="Inches"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Detailed Tables */}
              <Row className="mb-4">
                {/* Printer Details */}
                <Col md={6}>
                  <Card>
                    <Card.Header>Printer Performance Details</Card.Header>
                    <Card.Body
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <Table striped bordered hover size="sm">
                        <thead className="sticky-top bg-light">
                          <tr>
                            <th>Printer</th>
                            <th className="text-end">Orders</th>
                            <th className="text-end">Inches</th>
                            <th className="text-end">Avg/Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.by_printer.map((printer, idx) => (
                            <tr key={idx}>
                              <td>
                                {printer.printer === "Not Printed" ? (
                                  <Badge bg="warning" text="dark">
                                    {printer.printer}
                                  </Badge>
                                ) : (
                                  printer.printer
                                )}
                              </td>
                              <td className="text-end">
                                {formatNumber(printer.order_count)}
                                <small className="text-muted ms-1">
                                  ({printer.percentage_of_orders}%)
                                </small>
                              </td>
                              <td className="text-end">
                                {formatNumber(printer.total_inches)}
                              </td>
                              <td className="text-end">
                                {formatNumber(printer.avg_inches_per_order)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Order Type Details */}
                <Col md={6}>
                  <Card>
                    <Card.Header>Order Type Details</Card.Header>
                    <Card.Body
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <Table striped bordered hover size="sm">
                        <thead className="sticky-top bg-light">
                          <tr>
                            <th>Order Type</th>
                            <th className="text-end">Orders</th>
                            <th className="text-end">Inches</th>
                            <th className="text-end">Avg/Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.by_order_type.map((type, idx) => (
                            <tr key={idx}>
                              <td>{type.order_type}</td>
                              <td className="text-end">
                                {formatNumber(type.order_count)}
                                <small className="text-muted ms-1">
                                  ({type.percentage_of_orders}%)
                                </small>
                              </td>
                              <td className="text-end">
                                {formatNumber(type.total_inches)}
                              </td>
                              <td className="text-end">
                                {formatNumber(type.avg_inches_per_order)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Issues Section */}
              {reportData.issues &&
                reportData.issues.summary.total_issues > 0 && (
                  <Row className="mb-4">
                    <Col md={12}>
                      <Card border="danger">
                        <Card.Header className="bg-danger text-white">
                          <FiAlertCircle className="me-2" />
                          Data Quality Issues (
                          {reportData.issues.summary.total_issues})
                        </Card.Header>
                        <Card.Body>
                          <Row>
                            {/* Duplicates */}
                            {reportData.issues.duplicate_pos.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Duplicate POs (
                                  {reportData.issues.duplicate_pos.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                        <th>Count</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.duplicate_pos.map(
                                        (dup, idx) => (
                                          <tr key={idx}>
                                            <td>{dup.po_number}</td>
                                            <td>
                                              <Badge bg="danger">
                                                {dup.count}
                                              </Badge>
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}

                            {/* Missing Data */}
                            {reportData.issues.missing_data.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Missing Data (
                                  {reportData.issues.missing_data.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                        <th>Missing Fields</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.missing_data.map(
                                        (missing, idx) => (
                                          <tr key={idx}>
                                            <td>{missing.po_number}</td>
                                            <td>
                                              {missing.missing_fields.join(
                                                ", ",
                                              )}
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}

                            {/* Late Orders */}
                            {reportData.issues.late_orders.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Late Orders (Not Printed) (
                                  {reportData.issues.late_orders.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                        <th>Days Late</th>
                                        <th>Commitment Date</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.late_orders.map(
                                        (late, idx) => (
                                          <tr key={idx}>
                                            <td>{late.po_number}</td>
                                            <td>
                                              <Badge bg="danger">
                                                {late.days_late} days
                                              </Badge>
                                            </td>
                                            <td>{late.commitment_date}</td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}

                            {/* Orphaned Links */}
                            {reportData.issues.orphaned_links.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Orphaned Links (
                                  {reportData.issues.orphaned_links.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.orphaned_links.map(
                                        (orphan, idx) => (
                                          <tr key={idx}>
                                            <td>{orphan.po_number}</td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}

              {/* Success Message if no issues */}
              {reportData.issues &&
                reportData.issues.summary.total_issues === 0 && (
                  <Alert variant="success">
                    <FiCheckCircle className="me-2" />
                    No data quality issues found in this report period!
                  </Alert>
                )}
            </>
          )}
        </Tab>

        {/* Oklahoma location - Full content */}
        <Tab eventKey="oklahoma" title="Oklahoma">
          {/* Filters */}
          <Card className="mb-4">
            <Card.Body>
              <Row className="align-items-end">
                <Col md={3}>
                  <label className="form-label fw-bold">Report Type</label>
                  <ButtonGroup className="w-100">
                    {filterOptions.map((filter) => (
                      <Button
                        key={filter}
                        variant={
                          selectedFilter === filter
                            ? "primary"
                            : "outline-primary"
                        }
                        onClick={() => setSelectedFilter(filter)}
                        size="sm"
                      >
                        {filterLabels[filter]}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Col>

                {selectedFilter !== "custom" && (
                  <Col md={3}>
                    <label className="form-label fw-bold">
                      Select Date Range
                    </label>
                    <DatePicker
                      selectsRange={true}
                      startDate={startDate}
                      endDate={endDate}
                      onChange={(update) => {
                        setStartDate(update[0]);
                        setEndDate(update[1]);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      isClearable={true}
                      placeholderText="Select date range"
                      dayClassName={(date) => {
                        if (!startDate || !endDate) return undefined;
                        const time = date.getTime();
                        const start = startDate.getTime();
                        const end = endDate.getTime();
                        if (time === start || time === end) {
                          return "react-datepicker__day--range-endpoint";
                        }
                        if (time > start && time < end) {
                          return "react-datepicker__day--in-range";
                        }
                        return undefined;
                      }}
                    />
                  </Col>
                )}

                {selectedFilter === "custom" && (
                  <>
                    <Col md={2}>
                      <label className="form-label fw-bold">Start Date</label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                      />
                    </Col>
                    <Col md={2}>
                      <label className="form-label fw-bold">End Date</label>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                      />
                    </Col>
                  </>
                )}

                <Col md={2}>
                  <label className="form-label fw-bold">Date Field</label>
                  <select
                    className="form-select"
                    value={dateField}
                    onChange={(e) => setDateField(e.target.value)}
                  >
                    <option value="commitment_date">Commitment Date</option>
                    <option value="email_date">Email Date</option>
                  </select>
                </Col>

                <Col md={2} className="d-flex gap-2">
                  {selectedFilter === "custom" && (
                    <Button
                      variant="success"
                      onClick={fetchReportData}
                      disabled={!startDate || !endDate || loading}
                    >
                      Generate Report
                    </Button>
                  )}
                  <Button
                    variant="outline-secondary"
                    onClick={exportToPDF}
                    disabled={!reportData}
                  >
                    <FiDownload className="me-1" />
                    Export PDF
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading report data...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <Alert variant="danger">
              <FiAlertCircle className="me-2" />
              {error}
            </Alert>
          )}

          {/* Report Content */}
          {!loading && !error && reportData && (
            <>
              {/* Summary Cards */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="text-center border-primary">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Total Orders</h6>
                      <h2 className="mb-0 text-primary">
                        {formatNumber(reportData.summary.total_orders)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-success">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Total Inches</h6>
                      <h2 className="mb-0 text-success">
                        {formatNumber(reportData.summary.total_inches)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-info">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Printed Orders</h6>
                      <h2 className="mb-0 text-info">
                        {formatNumber(reportData.summary.printed_orders)}
                      </h2>
                      <small className="text-muted">
                        {reportData.summary.print_completion_rate}% completion
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="text-center border-warning">
                    <Card.Body>
                      <h6 className="text-muted mb-2">Not Printed</h6>
                      <h2 className="mb-0 text-warning">
                        {formatNumber(reportData.summary.not_printed_orders)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Additional Stats */}
              <Row className="mb-4">
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Avg Orders/Day</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.avg_orders_per_day)}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Avg Inches/Day</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.avg_inches_per_day)}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Avg Inches/Order</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.avg_inches_per_order)}
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card>
                    <Card.Body>
                      <small className="text-muted">Shipped Orders</small>
                      <h4 className="mb-0">
                        {formatNumber(reportData.summary.shipped_orders)}{" "}
                        <small className="text-muted">
                          ({reportData.summary.shipping_completion_rate}%)
                        </small>
                      </h4>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Charts Row */}
              <Row className="mb-4">
                {/* Printer Distribution */}
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-primary text-white">
                      <AiOutlinePrinter className="me-2" />
                      Orders by Printer
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={reportData.by_printer}>
                          <XAxis
                            dataKey="printer"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                          />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="order_count" fill="#4DABF7">
                            {reportData.by_printer.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                            <LabelList dataKey="order_count" position="top" />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Order Type Distribution */}
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-success text-white">
                      Orders by Type
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={reportData.by_order_type}
                            dataKey="order_count"
                            nameKey="order_type"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={(entry) =>
                              `${entry.order_type}: ${entry.order_count}`
                            }
                          >
                            {reportData.by_order_type.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Inches by Printer */}
              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-info text-white">
                      Inches Printed by Printer
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={reportData.by_printer}>
                          <XAxis
                            dataKey="printer"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                          />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="total_inches" fill="#51CF66">
                            {reportData.by_printer.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                            <LabelList
                              dataKey="total_inches"
                              position="top"
                              formatter={(value) => value.toFixed(0)}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Inches by Order Type */}
                <Col md={6}>
                  <Card>
                    <Card.Header className="bg-warning text-dark">
                      Inches by Order Type
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={reportData.by_order_type}>
                          <XAxis
                            dataKey="order_type"
                            angle={-45}
                            textAnchor="end"
                            height={100}
                          />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="total_inches" fill="#FFA94D">
                            {reportData.by_order_type.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={getColor(index)}
                              />
                            ))}
                            <LabelList
                              dataKey="total_inches"
                              position="top"
                              formatter={(value) => value.toFixed(0)}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Daily Trend */}
              {reportData.daily_breakdown.length > 1 && (
                <Row className="mb-4">
                  <Col md={12}>
                    <Card>
                      <Card.Header className="bg-dark text-white">
                        Daily Trend - Orders & Inches
                      </Card.Header>
                      <Card.Body>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={reportData.daily_breakdown}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis yAxisId="left" />
                            <YAxis yAxisId="right" orientation="right" />
                            <Tooltip />
                            <Legend />
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="order_count"
                              stroke="#4DABF7"
                              strokeWidth={2}
                              name="Orders"
                            />
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey="total_inches"
                              stroke="#51CF66"
                              strokeWidth={2}
                              name="Inches"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Detailed Tables */}
              <Row className="mb-4">
                {/* Printer Details */}
                <Col md={6}>
                  <Card>
                    <Card.Header>Printer Performance Details</Card.Header>
                    <Card.Body
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <Table striped bordered hover size="sm">
                        <thead className="sticky-top bg-light">
                          <tr>
                            <th>Printer</th>
                            <th className="text-end">Orders</th>
                            <th className="text-end">Inches</th>
                            <th className="text-end">Avg/Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.by_printer.map((printer, idx) => (
                            <tr key={idx}>
                              <td>
                                {printer.printer === "Not Printed" ? (
                                  <Badge bg="warning" text="dark">
                                    {printer.printer}
                                  </Badge>
                                ) : (
                                  printer.printer
                                )}
                              </td>
                              <td className="text-end">
                                {formatNumber(printer.order_count)}
                                <small className="text-muted ms-1">
                                  ({printer.percentage_of_orders}%)
                                </small>
                              </td>
                              <td className="text-end">
                                {formatNumber(printer.total_inches)}
                              </td>
                              <td className="text-end">
                                {formatNumber(printer.avg_inches_per_order)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Order Type Details */}
                <Col md={6}>
                  <Card>
                    <Card.Header>Order Type Details</Card.Header>
                    <Card.Body
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <Table striped bordered hover size="sm">
                        <thead className="sticky-top bg-light">
                          <tr>
                            <th>Order Type</th>
                            <th className="text-end">Orders</th>
                            <th className="text-end">Inches</th>
                            <th className="text-end">Avg/Order</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.by_order_type.map((type, idx) => (
                            <tr key={idx}>
                              <td>{type.order_type}</td>
                              <td className="text-end">
                                {formatNumber(type.order_count)}
                                <small className="text-muted ms-1">
                                  ({type.percentage_of_orders}%)
                                </small>
                              </td>
                              <td className="text-end">
                                {formatNumber(type.total_inches)}
                              </td>
                              <td className="text-end">
                                {formatNumber(type.avg_inches_per_order)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Issues Section */}
              {reportData.issues &&
                reportData.issues.summary.total_issues > 0 && (
                  <Row className="mb-4">
                    <Col md={12}>
                      <Card border="danger">
                        <Card.Header className="bg-danger text-white">
                          <FiAlertCircle className="me-2" />
                          Data Quality Issues (
                          {reportData.issues.summary.total_issues})
                        </Card.Header>
                        <Card.Body>
                          <Row>
                            {/* Duplicates */}
                            {reportData.issues.duplicate_pos.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Duplicate POs (
                                  {reportData.issues.duplicate_pos.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                        <th>Count</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.duplicate_pos.map(
                                        (dup, idx) => (
                                          <tr key={idx}>
                                            <td>{dup.po_number}</td>
                                            <td>
                                              <Badge bg="danger">
                                                {dup.count}
                                              </Badge>
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}

                            {/* Missing Data */}
                            {reportData.issues.missing_data.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Missing Data (
                                  {reportData.issues.missing_data.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                        <th>Missing Fields</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.missing_data.map(
                                        (missing, idx) => (
                                          <tr key={idx}>
                                            <td>{missing.po_number}</td>
                                            <td>
                                              {missing.missing_fields.join(
                                                ", ",
                                              )}
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}

                            {/* Late Orders */}
                            {reportData.issues.late_orders.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Late Orders (Not Printed) (
                                  {reportData.issues.late_orders.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                        <th>Days Late</th>
                                        <th>Commitment Date</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.late_orders.map(
                                        (late, idx) => (
                                          <tr key={idx}>
                                            <td>{late.po_number}</td>
                                            <td>
                                              <Badge bg="danger">
                                                {late.days_late} days
                                              </Badge>
                                            </td>
                                            <td>{late.commitment_date}</td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}

                            {/* Orphaned Links */}
                            {reportData.issues.orphaned_links.length > 0 && (
                              <Col md={6} className="mb-3">
                                <h6>
                                  Orphaned Links (
                                  {reportData.issues.orphaned_links.length})
                                </h6>
                                <div
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  <Table size="sm" striped>
                                    <thead>
                                      <tr>
                                        <th>PO Number</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {reportData.issues.orphaned_links.map(
                                        (orphan, idx) => (
                                          <tr key={idx}>
                                            <td>{orphan.po_number}</td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </Table>
                                </div>
                              </Col>
                            )}
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}

              {/* Success Message if no issues */}
              {reportData.issues &&
                reportData.issues.summary.total_issues === 0 && (
                  <Alert variant="success">
                    <FiCheckCircle className="me-2" />
                    No data quality issues found in this report period!
                  </Alert>
                )}
            </>
          )}
        </Tab>
      </Tabs>
    </div>
  );
};

export default JiffyReportsPage;
